import PlusCircleOutlined from '@ant-design/icons/PlusCircleOutlined'
import { ConfigProvider, Input, List, Spin } from 'antd'
import CantileverButton from 'components/CantileverButton'
import CantileverCard from 'components/CantileverCard'
import dayjs from 'dayjs'
import { CommentItem } from 'features/assetDatabase'
import { useRolodexEdit } from 'features/fde'
import { useGeckoPermissions } from 'features/permissions'
import { useRolodexEntries } from 'features/rolodex'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { useGeckoTheme } from 'styles/GeckoTheme'
import { RolodexEntryType } from 'types'
import { v4 as uuid4 } from 'uuid'

const CommentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 20rem;
  overflow-y: auto;
`

const NoCommentsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2.875rem;
  align-self: stretch;
  color: ${({ theme }) => theme.forecast.tertiaryText};
`

const AddCommentContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 1rem;
  height: auto;
  padding: 0.5rem;
`

const CardContainer = styled.div`
  width: 100%;
`

export interface WalkdownCommentSectionProps {
  walkdownId: string
  refreshData: () => Promise<void>
  style?: React.CSSProperties
}

export const WalkdownCommentSection: React.FC<WalkdownCommentSectionProps> = ({
  walkdownId,
  refreshData,
  style,
}) => {
  const { theme } = useGeckoTheme()
  const { rolodexPut } = useRolodexEdit()
  const { user: currentUser } = useGeckoPermissions()
  const [isLoading, setIsLoading] = useState(false)
  const [newComment, setNewComment] = useState<string | undefined>(undefined)

  const {
    userComments: userCommentEntries,
    isLoading: isLoadingComments,
    forceRefresh: refreshComments,
  } = useRolodexEntries({
    type: [RolodexEntryType.USER_COMMENT],
    tags: {
      [RolodexEntryType.WALKDOWN]: walkdownId,
    },
    order_by: [{ key: 'timestamp', direction: 'desc' }],
  })

  const userComments = useMemo(() => {
    return [...userCommentEntries]
  }, [userCommentEntries])

  const handleAddComment = async (comment: string | undefined) => {
    if (!comment || comment.length === 0 || !currentUser?.id) {
      console.error('Missing required data for comment:', {
        comment,
        walkdownId,
        userId: currentUser?.id,
      })
      return
    }

    setIsLoading(true)

    const commentId = uuid4()
    const currentTimestamp = dayjs().unix()

    try {
      await rolodexPut([
        {
          id: commentId,
          type: RolodexEntryType.USER_COMMENT,
          data: {
            user_id: currentUser.id,
            user_name: currentUser.name,
            text: comment,
            timestamp: currentTimestamp,
          },
          tags: {
            [RolodexEntryType.WALKDOWN]: walkdownId,
          },
        },
      ])
      await refreshData()
    } catch (error) {
      console.error('Error adding comment:', error)
    } finally {
      setIsLoading(false)
      setNewComment(undefined)
    }
  }

  return (
    <CardContainer style={style}>
      <CantileverCard>
        <CantileverCard.CardHeader leadingContent='Comments' />
        <CantileverCard.Section topPadding>
          <CantileverCard.Group>
            <CommentsContainer>
              <ConfigProvider
                renderEmpty={() => (
                  <NoCommentsContainer>No comments</NoCommentsContainer>
                )}
                theme={{
                  components: {
                    List: { colorSplit: theme.forecast.background3 },
                  },
                }}>
                <List
                  itemLayout='horizontal'
                  dataSource={userComments}
                  renderItem={(item) => (
                    <CommentItem
                      comment={item}
                      forceRefresh={refreshComments}
                    />
                  )}
                />
              </ConfigProvider>
            </CommentsContainer>
          </CantileverCard.Group>
        </CantileverCard.Section>
        <Spin spinning={isLoading || isLoadingComments}>
          <CantileverCard.Section>
            <CantileverCard.Group>
              <AddCommentContainer>
                <Input
                  variant='borderless'
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder='Add a comment...'
                  disabled={isLoading || isLoadingComments || !currentUser?.id}
                />
                <CantileverButton
                  style={{ width: '45%', minWidth: '8rem', margin: '0.5rem' }}
                  onClick={() => handleAddComment(newComment)}
                  disabled={isLoading || isLoadingComments || !currentUser?.id}>
                  <PlusCircleOutlined />
                  Add Comment
                </CantileverButton>
              </AddCommentContainer>
            </CantileverCard.Group>
          </CantileverCard.Section>
        </Spin>
      </CantileverCard>
    </CardContainer>
  )
}
